import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Share2, Users, UserCheck, IndianRupee, TrendingUp, 
  Copy, Check, MessageCircle, Send, ChevronRight, 
  Sparkles, Gift, BookOpen
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ShareEarningsCounter = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchShareStats();
  }, []);

  const fetchShareStats = async () => {
    try {
      const response = await api.get('/referral/share-earnings');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch share stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildShareLink = () => {
    const baseUrl = window.location.origin;
    const referralId = user?.username || user?.id || '';
    return `${baseUrl}/register?ref=${referralId}&guide=hindi`;
  };

  const shareText = `ANANT WEB PORTAL - Complete System Guide with Marketing Tips!\n\nLearn how to earn money online:\n- Affiliate Network System\n- P2P Marketplace\n- Auctions\n- Franchise Business\n- Free Business Tools\n\nJoin using my link and get FREE support:`;

  const handleCopyLink = async () => {
    const link = buildShareLink();
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${link}`);
      setCopied(true);
      toast.success('Guide link copied with your referral ID!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleShareWhatsApp = () => {
    const link = buildShareLink();
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${link}`)}`;
    window.open(url, '_blank');
  };

  const handleShareTelegram = () => {
    const link = buildShareLink();
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="glass-card p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-20 bg-white/10 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card overflow-hidden border-2 border-green-500/30" data-testid="share-earnings-counter">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 p-4 border-b border-green-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                Share Earnings Counter
                <Sparkles className="w-4 h-4 text-yellow-500" />
              </h3>
              <p className="text-sm text-muted-foreground">Track your referral performance</p>
            </div>
          </div>
          <Link 
            to="/commissions" 
            className="flex items-center gap-1 text-sm text-green-500 hover:text-green-400 transition-colors"
          >
            View Details <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
            <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-500">{stats?.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Total Referrals</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
            <UserCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-500">{stats?.activated_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Activated (Paid)</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
            <Gift className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-500">{stats?.pending_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Pending Activation</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-4 text-center border border-green-500/30">
            <IndianRupee className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-400">₹{(stats?.commission_earned || 0).toLocaleString()}</p>
            <p className="text-xs text-green-300">Commission Earned</p>
          </div>
        </div>

        {/* Recent Activity */}
        {(stats?.recent_referrals_count > 0 || stats?.recent_activations_count > 0) && (
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-4 mb-6">
            <p className="text-sm font-medium mb-2">📈 Last 7 Days Activity</p>
            <div className="flex items-center gap-6 text-sm">
              <span className="text-blue-400">
                <strong>+{stats?.recent_referrals_count || 0}</strong> new signups
              </span>
              <span className="text-green-400">
                <strong>+{stats?.recent_activations_count || 0}</strong> activations
              </span>
            </div>
          </div>
        )}

        {/* Quick Share Section */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-primary" />
            <p className="text-sm font-medium">Quick Share Guide with Referral Link</p>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={handleCopyLink}
              className="flex flex-col items-center gap-2 p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all hover:scale-105"
              data-testid="copy-share-link"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5 text-primary" />
              )}
              <span className="text-xs">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            
            <button
              onClick={handleShareWhatsApp}
              className="flex flex-col items-center gap-2 p-3 bg-green-500/10 hover:bg-green-500/20 rounded-xl transition-all hover:scale-105"
              data-testid="share-wa"
            >
              <MessageCircle className="w-5 h-5 text-green-500" />
              <span className="text-xs">WhatsApp</span>
            </button>
            
            <button
              onClick={handleShareTelegram}
              className="flex flex-col items-center gap-2 p-3 bg-blue-500/10 hover:bg-blue-500/20 rounded-xl transition-all hover:scale-105"
              data-testid="share-tg"
            >
              <Send className="w-5 h-5 text-blue-400" />
              <span className="text-xs">Telegram</span>
            </button>
          </div>
          
          {/* Pro Tip */}
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
            <p className="text-xs text-yellow-500">
              <strong>💡 Pro Tip:</strong> Share the Hindi System Guide daily on WhatsApp status. 
              When people register using your link, you earn <strong>10% commission</strong> on their activation!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareEarningsCounter;
